import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { QueryClient } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import type { AnyRouter } from "@tanstack/react-router";

import { queryKeys } from "@/lib/api/query";
import { currentSession, logout } from "@/lib/api/session";
import type { SessionUser } from "@/lib/api/session";
import { clearBranchContext } from "@/lib/branch/branch-store";
import { CORE, type AppCode } from "@/lib/modules";

export interface UseSessionResult {
  /** The signed-in user, or null when anonymous (or still loading). */
  user: SessionUser | null;
  /**
   * The company the session belongs to, or null when anonymous. Its
   * `base_currency` is the ledger's own; it is null when the company has no
   * ledger settings row, and a screen that needs a currency must say so
   * rather than assume one.
   */
  company: SessionUser["company"] | null;
  /** True only while the initial GET /session probe is in flight. */
  isLoading: boolean;
  /** Exact-match permission check against the session's permission list. */
  hasPermission: (permission: string) => boolean;
  /**
   * Whether the company has installed an app.
   *
   * This is a different question from `hasPermission`, and the screens keep
   * them apart: "you are not allowed to do this" is fixed by an administrator
   * granting a role, and "your company does not have this app" is fixed by
   * installing it. Telling somebody the first when the second is true sends
   * them to the wrong person.
   *
   * Core is always installed, so it answers true even before the list loads.
   */
  hasModule: (module: AppCode) => boolean;
  /** The apps this company has installed. */
  installedModules: readonly AppCode[];
  /** DELETE /session, drop every cache, land on /login. */
  signOut: () => Promise<void>;
}

/**
 * Session state for the whole app. The probe result is cached forever
 * (staleTime Infinity) — login/logout/expiry mutate or clear the cache
 * explicitly instead of relying on background refetches.
 */
export function useSession(): UseSessionResult {
  const queryClient = useQueryClient();
  const router = useRouter();

  const query = useQuery({
    queryKey: queryKeys.session.current(),
    queryFn: () => currentSession(),
    staleTime: Infinity,
  });

  const user = query.data ?? null;

  return {
    user,
    company: user?.company ?? null,
    isLoading: query.isPending,
    hasPermission: (permission) => user?.permissions.includes(permission) ?? false,
    hasModule: (module) => module === CORE || (user?.installed_modules?.includes(module) ?? false),
    installedModules: user?.installed_modules ?? [],
    signOut: async () => {
      try {
        await logout();
      } finally {
        // Even if the DELETE failed, drop local state — the user asked to
        // leave, and any privileged data must not outlive that intent.
        queryClient.clear();
        await router.navigate({ to: "/login" });
      }
    },
  };
}

let removeExpiryListener: (() => void) | null = null;

/**
 * Wire the global "erp:unauthorized" event (dispatched by the API layer on
 * any unexpected 401) to session-expiry handling: clear the cached session
 * and land on /login with the "expired" banner. Call once at boot, after
 * both the QueryClient and the router exist. Re-registering replaces the
 * previous listener, so repeated boots (tests, HMR) never stack handlers.
 * Returns an unsubscribe function.
 */
export function registerSessionExpiryListener(
  queryClient: QueryClient,
  router: AnyRouter,
): () => void {
  removeExpiryListener?.();
  const handler = (): void => {
    clearBranchContext();
    queryClient.setQueryData(queryKeys.session.current(), null);
    void router.navigate({ to: "/login", search: { reason: "expired" } });
  };
  window.addEventListener("erp:unauthorized", handler);
  const unsubscribe = (): void => {
    window.removeEventListener("erp:unauthorized", handler);
    if (removeExpiryListener === unsubscribe) removeExpiryListener = null;
  };
  removeExpiryListener = unsubscribe;
  return unsubscribe;
}
